import React from 'react';
import { Navigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  Stack,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import Layout from '../../components/Layout';
import { PageHeader } from '../../components/shared/page-header';
import { customBaseUrl, httpPost } from '../../store/http';
import { showLoader, hideLoader } from '../../utils/loader';
import Swal from 'sweetalert2';
import slugs from '../../resources/slugs';

const RegisterDisputeAdmin = () => {
  const [showPassword, setShowPassword] = React.useState(false);
  const rawRoles = localStorage.getItem('roles');
  let userRoles = [];
  try {
    userRoles = JSON.parse(rawRoles || '[]');
    if (!Array.isArray(userRoles)) {
      userRoles = [userRoles];
    }
  } catch {
    userRoles = rawRoles ? [rawRoles] : [];
  }

  const isAuthorized = userRoles.some(role => ['SUPER_ADMIN', 'OWNER', 'USER'].includes(role));

  const formik = useFormik({
    initialValues: {
      username: '',
      email: '',
      password: '',
    },
    validationSchema: Yup.object({
      username: Yup.string().required('Username is required'),
      email: Yup.string().email('Invalid email address').required('Email is required'),
      password: Yup.string()
        .min(8, 'Password must be at least 8 characters')
        .required('Password is required'),
    }),
    onSubmit: async (values) => {
      showLoader();
      try {
        const response = await httpPost(
          `${customBaseUrl.disputeServiceV3}/api/v1/admin/users/create`,
          values
        );
        hideLoader();
        if (response.success || response.status) {
          Swal.fire('Success', 'Dispute Management Admin created successfully', 'success');
          formik.resetForm();
        } else {
          Swal.fire('Error', response.message || 'Failed to create admin', 'error');
        }
      } catch (error) {
        hideLoader();
        Swal.fire('Error', 'An unexpected error occurred', 'error');
      }
    },
  });

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  if (!isAuthorized) {
    return <Navigate to={slugs.dashboard} />;
  }

  return (
    <Layout>
      <Box sx={{ p: 4 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
          <PageHeader
            title="Register Dispute Management Admin"
            description="Create a new administrative account for dispute management"
          />
        </Stack>

        <Paper sx={{ p: 4, maxWidth: 600, mx: 'auto' }}>
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="subtitle1" fontWeight="medium" mb={1}>
                  Username
                </Typography>
                <TextField
                  fullWidth
                  id="username"
                  name="username"
                  placeholder="Enter username"
                  value={formik.values.username}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.username && Boolean(formik.errors.username)}
                  helperText={formik.touched.username && formik.errors.username}
                  size="small"
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle1" fontWeight="medium" mb={1}>
                  Email Address
                </Typography>
                <TextField
                  fullWidth
                  id="email"
                  name="email"
                  placeholder="Enter email address"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                  size="small"
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle1" fontWeight="medium" mb={1}>
                  Password
                </Typography>
                <TextField
                  fullWidth
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter password"
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.password && Boolean(formik.errors.password)}
                  helperText={formik.touched.password && formik.errors.password}
                  size="small"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sx={{ mt: 2 }}>
                <Button
                  fullWidth
                  variant="contained"
                  type="submit"
                  sx={{
                    py: 1.5,
                    backgroundColor: '#FF4B01',
                    '&:hover': {
                      backgroundColor: '#E64401',
                    },
                  }}
                >
                  Register Admin
                </Button>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Box>
    </Layout>
  );
};
export default RegisterDisputeAdmin;
